/**
 * Player Knowledge Coverage dashboard.
 *
 * Shows what % of each taxonomy category the player has been exposed to,
 * driven by `GET /api/nke/coverage/me`. This is Knowledge, not points —
 * it exists to encourage exploration outside the player's comfort zone.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Books, ArrowLeft, Target } from "@phosphor-icons/react";

export default function KnowledgeCoverage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.get("/nke/coverage/me")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load coverage"))
      .finally(() => setBusy(false));
  }, []);

  const sorted = (data?.categories || []).slice().sort((a, b) => b.total - a.total);
  const weakest = sorted.filter((c) => c.total > 0 && c.pct < 40).slice(0, 3);
  const strongest = sorted.filter((c) => c.total > 0 && c.pct >= 70).slice(0, 3);

  return (
    <div className="max-w-3xl space-y-6" data-testid="knowledge-coverage">
      <div className="flex items-center gap-3 text-sm text-white/60">
        <Link to="/play" className="hover:text-nk-gold inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Play
        </Link>
      </div>

      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/85 font-medium mb-2 flex items-center gap-2">
          <Books size={13} weight="fill" />
          Your knowledge
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">
          The <span className="italic font-light text-nk-gold">library</span> you&apos;ve explored
        </h1>
        <p className="text-sm text-white/60 mt-2 max-w-xl">
          Every game teaches you something. This is how much of NKognito&apos;s knowledge universe
          you&apos;ve encountered so far — not points, just exploration.
        </p>
      </header>

      {busy && <div className="text-sm text-white/60">Loading…</div>}

      {data && (
        <>
          <div className="border border-nk-line rounded-sm p-4 flex items-center gap-4">
            <Target size={22} weight="duotone" className="text-nk-gold" />
            <div>
              <p className="text-2xl font-display text-nk-gold">{data.total_facts_seen}</p>
              <p className="text-[11px] uppercase tracking-widest text-white/50">
                distinct knowledge objects seen
              </p>
            </div>
          </div>

          {weakest.length > 0 && (
            <div className="border border-nk-line rounded-sm p-4"
                 data-testid="coverage-weakest">
              <p className="text-[11px] uppercase tracking-widest text-nk-gold mb-2">Room to grow</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {weakest.map((c) => (
                  <span key={c.category}
                        className="px-2.5 py-1 border border-nk-gold/40 rounded-sm">
                    {c.emoji} {c.category} · <b className="text-nk-gold">{c.pct}%</b>
                  </span>
                ))}
              </div>
              <p className="text-xs text-white/50 mt-2">
                Play a mode that touches these areas to bump your coverage.
              </p>
            </div>
          )}

          <div className="space-y-1.5" data-testid="coverage-bars">
            {sorted.map((c) => (
              <CategoryBar key={c.category} c={c} />
            ))}
          </div>

          {strongest.length > 0 && (
            <div className="border border-nk-line rounded-sm p-4"
                 data-testid="coverage-strongest">
              <p className="text-[11px] uppercase tracking-widest text-emerald-400 mb-2">Your strong areas</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {strongest.map((c) => (
                  <span key={c.category}
                        className="px-2.5 py-1 border border-emerald-400/40 text-emerald-400 rounded-sm">
                    {c.emoji} {c.category} · {c.pct}%
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CategoryBar({ c }) {
  const pct = Math.min(100, Math.max(0, c.pct));
  const empty = c.total === 0;
  return (
    <div className="flex items-center gap-3 text-sm py-2 border-b border-nk-line/40"
         data-testid={`coverage-cat-${c.category.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`}>
      <span className="w-6 text-lg">{c.emoji}</span>
      <span className="w-40 shrink-0 text-white/85">{c.category}</span>
      <div className="flex-1 relative h-2 bg-white/8 rounded-sm overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-nk-gold transition-all"
             style={{ width: `${pct}%`, opacity: empty ? 0.15 : 1 }} />
      </div>
      <span className={`w-24 text-right font-mono text-xs ${empty ? "text-white/30" : "text-white/70"}`}>
        {c.seen}/{c.total}
      </span>
      <span className={`w-12 text-right font-mono text-xs ${empty ? "text-white/30" : "text-nk-gold"}`}>
        {c.pct}%
      </span>
    </div>
  );
}
